import type { Directive } from 'vue';
import { watch, type WatchStopHandle } from 'vue';
import type { Router } from 'vue-router';

type Binding = { articleId: number | string; classes: string | string[] };

type ElementState = { articleId: string; classes: string[]; stop: WatchStopHandle };
const states = new WeakMap<HTMLElement, ElementState>();

const toClassList = (classes: Binding['classes']) => ([] as string[]).concat(classes);

function apply(el: HTMLElement, classes: string[], open: boolean) {
  for (const name of classes) el.classList.toggle(name, open);
}

/**
 * Usage: v-open-article="{ articleId: article.id, classes: 'row--open' }"
 *
 * Marks the row/card whose article is open in the details panel, WITHOUT the surrounding
 * list template ever reading the route. That distinction is the whole point: a route read in
 * a row template becomes a dependency of whichever render effect draws the list — for a
 * virtual scroller, the scroller itself — so stepping the panel's pager from one article to
 * the next re-rendered every visible row and every cell in it. Here the route is watched per
 * element and the class is toggled on the node directly, so a navigation costs two class
 * toggles instead of a full list render.
 *
 * A directive rather than a wrapper component because these lists render their rows through
 * vue-virtual-scroller and vuedraggable, and vuedraggable requires each item slot to hold
 * exactly one plain element it can move.
 *
 * Takes the router explicitly: `binding.instance` is whatever proxy Vue was rendering, and
 * for a `<script setup>` component with `defineExpose` that proxy exposes only the exposed
 * keys — `$route` isn't reachable through it.
 */
export const openArticleDirective = (router: Router): Directive<HTMLElement, Binding> => ({
  mounted(el, binding) {
    const state: ElementState = {
      articleId: String(binding.value.articleId),
      classes: toClassList(binding.value.classes),
      stop: () => {},
    };
    states.set(el, state);

    // The watcher reads the element's state rather than the binding it was created with, so
    // a row the virtual scroller recycles for another article resolves against that
    // article — `updated` below keeps the state current.
    state.stop = watch(
      () => router.currentRoute.value.params.articleId,
      (openId) => apply(el, state.classes, state.articleId === openId),
      { immediate: true }
    );
  },

  updated(el, binding) {
    const state = states.get(el);
    if (!state) return;

    const classes = toClassList(binding.value.classes);
    // Clear any class that is no longer part of the binding before the new list takes over.
    apply(
      el,
      state.classes.filter((name) => !classes.includes(name)),
      false
    );

    state.classes = classes;
    state.articleId = String(binding.value.articleId);
    apply(el, classes, state.articleId === router.currentRoute.value.params.articleId);
  },

  unmounted(el) {
    states.get(el)?.stop();
    states.delete(el);
  },
});
